import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dealAPI, productAPI } from '../../api/api';
import { useApi } from '../../api/useApi';
import { useCartStore } from '../../store/cartStore';
import useIsMobile from '../../hooks/useIsMobile';
import { useAuthStore } from '../../store/authStore';
import { buildDealCheckoutItems, buildDealPackageCheckoutItem, summarizeDealPricing } from '../../utils/commerce';
import FarmLifecycleLoader from '../common/FarmLifecycleLoader';

const BADGE_STYLES = [
  { label: 'Best Seller', color: '#ef4444' },
  { label: 'Best Value', color: '#f59e0b' },
  { label: 'Fan Favourite', color: '#8b5cf6' },
  { label: 'Fresh Pick', color: '#0ea5e9' },
];

const resolveDealProducts = (deal = {}) => {
  if (Array.isArray(deal.products)) return deal.products;
  if (Array.isArray(deal.productIds)) return deal.productIds;
  return [];
};

const formatCurrency = (value) => `Rs.${Number(value || 0).toLocaleString('en-IN')}`;

const buildItemsPreview = (products = []) => {
  const names = products
    .map((product) => String(product?.name || '').trim())
    .filter(Boolean);

  if (names.length <= 5) return names;
  return [...names.slice(0, 4), `+${names.length - 4} more farm picks`];
};

const buildDescription = (deal, products) => {
  const occasion = String(deal?.occasion || '').trim();
  const categories = [...new Set(products.map((product) => product?.category).filter(Boolean))];

  if (occasion && categories.length > 0) {
    return `${occasion} bundle with ${categories.join(', ').toLowerCase()} from verified farmers`;
  }

  if (occasion) {
    return `${occasion} bundle curated by the Sedyam deals team`;
  }

  return `${products.length} handpicked products packed into one farm-direct bundle`;
};

export default function FoodPackages() {
  const navigate = useNavigate();
  const location = useLocation();
  const addItem = useCartStore((store) => store.addItem);
  const userProfile = useAuthStore((store) => store.userProfile);
  const isMobile = useIsMobile();
  const [added, setAdded] = useState({});
  const { data: deals, loading, error } = useApi(dealAPI.getPublic, { limit: 3 }, {
    pollInterval: 15000,
    keepPreviousData: true,
  });
  const { data: productCatalog, loading: productsLoading } = useApi(productAPI.getAll, {}, {
    pollInterval: 30000,
    keepPreviousData: true,
  });

  const requireLogin = () => {
    if (userProfile) return true;
    navigate('/login', {
      state: {
        redirectTo: `${location.pathname}${location.search || ''}`,
        redirectState: location.state || null,
      },
    });
    return false;
  };

  const packageCards = useMemo(() => {
    const list = Array.isArray(deals) ? deals : [];
    const productMap = new Map(
      (Array.isArray(productCatalog) ? productCatalog : []).map((product) => [
        String(product?._id || product?.id || '').trim(),
        product,
      ])
    );

    return list
      .map((deal, index) => {
        const products = resolveDealProducts(deal)
          .map((product) => {
            const productId = String(product?._id || product?.id || '').trim();
            if (!productId) return product;
            const catalogProduct = productMap.get(productId);
            const productFarmerHasLocation = Boolean(product?.farmerId?.location);
            return catalogProduct
              ? {
                  ...catalogProduct,
                  ...product,
                  farmerId: productFarmerHasLocation ? product.farmerId : (catalogProduct.farmerId || product.farmerId),
                }
              : product;
          })
          .filter(Boolean);
        const enrichedDeal = { ...deal, products };
        const pricing = summarizeDealPricing(enrichedDeal);
        const checkoutItems = buildDealCheckoutItems(enrichedDeal, 1);
        const packageItem = buildDealPackageCheckoutItem(enrichedDeal, 1);
        const badge = BADGE_STYLES[index % BADGE_STYLES.length];
        const fullyAvailable =
          checkoutItems.length > 0 &&
          products.length > 0 &&
          products.every((product) => Number(product?.availableQuantityKg || 0) > 0) &&
          checkoutItems.every((item) => item.id && item.farmerId);

        return {
          id: String(deal?._id || deal?.id || `deal-${index}`),
          name: String(deal?.dealName || `Farm Package ${index + 1}`),
          description: buildDescription(deal, products),
          image:
            products[0]?.images?.[0] ||
            'https://images.unsplash.com/photo-1542838132-92c53300491e?w=400',
          items: buildItemsPreview(products),
          price: pricing.discountedTotal,
          originalPrice: pricing.originalTotal,
          savings: pricing.savings,
          badgeLabel: badge.label,
          badgeColor: badge.color,
          checkoutItems,
          packageItem,
          disabled: !fullyAvailable,
        };
      })
      .filter((deal) => deal.checkoutItems.length > 0);
  }, [deals, productCatalog]);

  const handleAddToCart = (deal) => {
    if (!requireLogin()) return;

    addItem(deal.packageItem, 1);

    setAdded((current) => ({ ...current, [deal.id]: true }));
    window.setTimeout(() => {
      setAdded((current) => ({ ...current, [deal.id]: false }));
    }, 1600);
  };

  const handleBuyNow = (deal) => {
    navigate('/checkout', {
      state: {
        buyNow: true,
        source: 'buy_now',
        checkoutItems: [deal.packageItem],
      },
    });
  };

  return (
    <section id="packages" style={{ padding: isMobile ? '56px 16px' : '80px 24px', backgroundColor: '#fefae0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '56px' }}>
          <span
            style={{
              background: '#d8f3dc',
              color: '#2d6a4f',
              padding: '6px 20px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            Food Packages
          </span>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '700', color: '#1a1a2e', marginTop: '16px' }}>
            Curated Farm Packages
          </h2>
          <p style={{ color: '#6b7280', marginTop: '12px', fontSize: '1.1rem' }}>
            Handpicked combinations at unbeatable farm-direct prices
          </p>
        </div>

        {loading && productsLoading && packageCards.length === 0 ? (
          <FarmLifecycleLoader
            compact
            title="Loading curated deals"
            subtitle="Fetching manager-created farm packages for the homepage..."
          />
        ) : null}

        {!loading && error ? (
          <div style={{ textAlign: 'center', padding: '24px', color: '#b91c1c' }}>
            Failed to load package deals: {error}
          </div>
        ) : null}

        {!loading && !error && packageCards.length === 0 ? (
          <div
            style={{
              background: 'rgba(255,255,255,0.8)',
              borderRadius: '24px',
              padding: isMobile ? '28px 22px' : '40px',
              textAlign: 'center',
              color: '#6b7280',
              boxShadow: '0 12px 34px rgba(15, 23, 42, 0.06)',
            }}
          >
            No live food package deals are available yet. Create one from the deals dashboard to feature it here.
          </div>
        ) : null}

        {packageCards.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 240 : 320}px, 1fr))`,
              gap: isMobile ? '16px' : '32px',
            }}
          >
            {packageCards.map((pkg) => (
              <div
                key={pkg.id}
                style={{
                  background: 'white',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  opacity: pkg.disabled ? 0.82 : 1,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.transform = 'translateY(-6px)';
                  event.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.transform = 'translateY(0)';
                  event.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.08)';
                }}
              >
                <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                  <img src={pkg.image} alt={pkg.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <span
                    style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      background: pkg.badgeColor,
                      color: 'white',
                      padding: '4px 14px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: '700',
                    }}
                  >
                    {pkg.badgeLabel}
                  </span>
                </div>

                <div style={{ padding: isMobile ? '16px' : '24px' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' }}>
                    {pkg.name}
                  </h3>
                  <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px', minHeight: '42px' }}>
                    {pkg.description}
                  </p>

                  <div style={{ background: '#f8fdf9', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                    {pkg.items.map((item, index) => (
                      <div
                        key={`${pkg.id}-item-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: index < pkg.items.length - 1 ? '8px' : 0,
                        }}
                      >
                        <span style={{ color: '#52b788', fontSize: '12px', fontWeight: '700' }}>v</span>
                        <span style={{ fontSize: '13px', color: '#374151' }}>{item}</span>
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: isMobile ? 'stretch' : 'flex-start',
                      justifyContent: 'space-between',
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? '12px' : '16px',
                    }}
                  >
                    <div>
                      <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#2d6a4f' }}>
                        {formatCurrency(pkg.price)}
                      </span>
                      <span
                        style={{
                          fontSize: '13px',
                          color: '#9ca3af',
                          textDecoration: 'line-through',
                          marginLeft: '8px',
                        }}
                      >
                        {formatCurrency(pkg.originalPrice)}
                      </span>
                      <p style={{ fontSize: '12px', color: '#ef4444', fontWeight: '600' }}>
                        Save {formatCurrency(pkg.savings)}
                      </p>
                    </div>

                    <div style={{ display: 'grid', gap: '8px', width: isMobile ? '100%' : 'auto' }}>
                      <button
                        onClick={() => handleAddToCart(pkg)}
                        disabled={pkg.disabled}
                        style={{
                          background: added[pkg.id] ? '#52b788' : '#2d6a4f',
                          color: 'white',
                          border: 'none',
                          padding: isMobile ? '11px 18px' : '12px 24px',
                          borderRadius: '999px',
                          fontWeight: '600',
                          fontSize: '14px',
                          cursor: pkg.disabled ? 'not-allowed' : 'pointer',
                          width: isMobile ? '100%' : 'auto',
                          opacity: pkg.disabled ? 0.6 : 1,
                        }}
                      >
                        {pkg.disabled ? 'Unavailable' : added[pkg.id] ? 'Added to Cart' : 'Add to Cart'}
                      </button>

                      {!pkg.disabled ? (
                        <button
                          onClick={() => handleBuyNow(pkg)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#2d6a4f',
                            fontSize: '13px',
                            fontWeight: '700',
                            cursor: 'pointer',
                          }}
                        >
                          Buy Now
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
